import React from 'react';

// Payment Options (issue #450) on a Prepay Customer's documents: the Payment
// strip on the Quote and Sales Order prints, and the Quote email's Payment
// Details field. Both read companySettings.payment_options, which the
// backend has already narrowed to each option that's on and complete
// (services/paymentOptionService.js).

// The fields an option's message can carry, each typed as {key} and filled
// in on the printed document.
export const PAYMENT_MESSAGE_FIELDS = [
  { key: 'document_number', label: 'the Quote or Sales Order number, e.g. Q-1042' },
];

// Stored as 6 bare digits, shown the way banks print it: "062-000".
export function formatBsb(bsb) {
  const digits = String(bsb || '').replace(/\D/g, '');
  return digits.length === 6 ? `${digits.slice(0, 3)}-${digits.slice(3)}` : (bsb || '');
}

// The message with each {field} filled in for this document.
function fillMessage(text, documentNumber) {
  if (!text) return '';
  return text.replace(/\{document_number\}/g, documentNumber || '');
}

// The Quote email's Payment Details field: one detail per line, with a blank
// line between options, and nothing once nothing is owing (issue #452).
export function paymentDetailLines(companySettings, quote) {
  if (!quote?.is_amount_owing) return [];
  const documentNumber = quote.display_number;
  const blocks = (companySettings?.payment_options || []).map((option) => {
    const lines = option.option_key === 'payid'
      ? [`PayID: ${option.payid}`, `PayID name: ${option.payid_name}`]
      : [
        `Account Name: ${option.account.bank_account_name}`,
        `BSB: ${formatBsb(option.account.bank_bsb)}`,
        `Account Number: ${option.account.bank_account_number}`,
      ];
    return [...lines, fillMessage(option.message, documentNumber)].filter(Boolean);
  });
  return blocks.flatMap((lines, i) => (i ? ['', ...lines] : lines));
}

const LABEL_STYLE = { color: '#888', fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', whiteSpace: 'nowrap', paddingTop: 1 };

// The Payment strip itself: full width under the totals, a rule above it
// like the lines table's own, each option's details on one line. Shows only
// for a Customer on a Prepay Payment Term while money is owing (issue #452),
// read live, so a change shows on every document printed or emailed after.
export function PaymentDetailsBox({ doc, companySettings }) {
  const options = companySettings?.payment_options || [];
  if (!doc?.customer?.payment_term?.is_prepay || !doc.is_amount_owing || !options.length) return null;
  const documentNumber = doc.display_number;
  return (
    <div style={{ marginTop: 20, paddingTop: 10, borderTop: '1px solid #ddd', display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 16, rowGap: 8, fontSize: 12, color: '#666', breakInside: 'avoid' }}>
      {options.map((option) => {
        const isPayid = option.option_key === 'payid';
        const details = isPayid
          ? [option.payid, option.payid_name]
          : [option.account.bank_account_name, `BSB ${formatBsb(option.account.bank_bsb)}`, `Acc ${option.account.bank_account_number}`];
        const message = fillMessage(option.message, documentNumber);
        return (
          <React.Fragment key={option.option_key}>
            {/* PayID keeps its own capitalisation inside the all-caps label. */}
            <div style={LABEL_STYLE}>Payment by {isPayid ? <span style={{ textTransform: 'none' }}>PayID</span> : 'Bank Transfer'}</div>
            <div>
              <div style={{ color: '#1a1a1a' }}>{details.join(' · ')}</div>
              {message && <div>{message}</div>}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}
